import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { compose } from 'redux';

import type { W_OrganizationCredential_FindOrganizationCredentials_VM_Row } from 'ab-api/web/organizationCredential/findAllForTable';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import type { TableQuery } from 'ab-common/dataStructures/tableQuery';

import ConfirmationModal from 'af-components/ConfirmationModal';
import Table from 'af-components/Table';
import type { TableRef } from 'af-components/Table';
import ActionsCell from 'af-components/Table/Cells/ActionsCell';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import type { TableProps } from 'af-components/Table/types';
import DateCell from 'af-components/Table6/Cells/DateCell';
import ElipsisCell from 'af-components/Table6/Cells/ElipsisCell';

import * as OrganizationCredentialActions from 'af-actions/organizationCredential';

import CredentialUpsertModal from './Modals/CredentialUpsertModal';

const DELETE_DOCUMENT_TEXT = 'Delete Item';

type Props = ConnectedProps<typeof connector>;

const OrganizationCredentials: React.FC<Props> = (props) => {
	const { findOrganizationCredentialsTable, deleteOrganizationCredentials } = props;

	const [credentialForDelete, setCredentialForDelete] = React.useState<Nullable<W_OrganizationCredential_FindOrganizationCredentials_VM_Row>>(null);
	const [showCredentialModal, setShowCredentialModal] = React.useState(false);
	const [credentialForUpdate, setCredentialForUpdate] = React.useState<Nullable<W_OrganizationCredential_FindOrganizationCredentials_VM_Row>>(null);

	const tableRef = React.useRef<TableRef<W_OrganizationCredential_FindOrganizationCredentials_VM_Row>>(null);

	const fetchData = React.useCallback(async (tableRequestModel: TableQuery) => {
		return await findOrganizationCredentialsTable(tableRequestModel);
	}, [findOrganizationCredentialsTable]);

	const handleRefreshTable = React.useCallback(() => {
		if (tableRef.current) {
			tableRef.current.refreshTable();
		}
	}, []);

	const openNewCredentialModal = React.useCallback(async () => {
		setShowCredentialModal(true);
	}, []);

	const openEditCredentialModel = React.useCallback(async (row: W_OrganizationCredential_FindOrganizationCredentials_VM_Row) => {
		setShowCredentialModal(true);
		setCredentialForUpdate(row);
	}, []);

	const closeUpsertCredentialModal = React.useCallback(async () => {
		setCredentialForUpdate(null);
		setShowCredentialModal(false);
	}, []);

	const openDeleteModal = React.useCallback((original: W_OrganizationCredential_FindOrganizationCredentials_VM_Row) => {
		setCredentialForDelete(original);
	}, []);

	const closeDeleteModal = React.useCallback(() => {
		setCredentialForDelete(null);
	}, []);

	const confirmDeletion = React.useCallback(async () => {
		if (credentialForDelete) {
			await deleteOrganizationCredentials(credentialForDelete.id);
			handleRefreshTable();
		}
	}, [credentialForDelete, deleteOrganizationCredentials, handleRefreshTable]);

	const confirmCreationModal = React.useCallback(() => {
		closeUpsertCredentialModal();
		handleRefreshTable();
	}, [closeUpsertCredentialModal, handleRefreshTable]);

	const resolveActionsButton = React.useCallback((_cell: CellContext<W_OrganizationCredential_FindOrganizationCredentials_VM_Row, unknown>) => {
		const actions = [
			{
				onClick: () => openEditCredentialModel(_cell.row.original),
				label: 'Edit',
				shouldRefresh: false,
			},
			{
				onClick: () => openDeleteModal(_cell.row.original),
				label: 'Delete',
				shouldRefresh: true,
			},
		];

		return (
			<ActionsCell
				id="actions"
				isActionDropdown={true}
				labelKey="label"
				options={actions}
				valueKey="label"
			/>
		);
	}, [openEditCredentialModel, openDeleteModal]);

	const columns: TableProps<W_OrganizationCredential_FindOrganizationCredentials_VM_Row>['columns'] = React.useMemo(() => [
		{
			id: 'name',
			header: 'Name',
			accessor: 'name',
			enableSorting: true,
			cell: ({ cell }) => (
				<>
					<div className={'text-strong'}>
						{cell.row.original.name}
					</div>
				</>
			),
		},
		{
			id: 'companies',
			header: 'Companies',
			accessor: 'companies',
			enableSorting: false,
			cell: ({ cell }) => <ElipsisCell message={cell.row.original.companies.map((_company) => _company.name).join(', ')} showToolTip={true} />,
		},
		{
			id: 'updatedAt',
			header: 'Updated',
			accessor: 'updatedAt',
			enableSorting: true,
			cell: ({ cell }) => <DateCell date={cell.row.original.updatedAt} format={TimeFormat.FULL_DATE_LONG} isLeftAligned={true} />,
		},
		{
			id: 'actions',
			isDisplayColumn: true,
			header: () => <EmptyCell isHeader />,
			cell: resolveActionsButton,
		},
	], [resolveActionsButton]);

	const buttons = React.useMemo(() => {
		return [
			{
				type: TableButtonType.PRIMARY,
				hasPermission: true,
				label: 'Add New Credentials',
				onClick: openNewCredentialModal,
			},
		];
	}, [openNewCredentialModal]);

	const deleteDocumentModalTitle = React.useMemo(() => (
		<>
			You are about delete "<strong>{credentialForDelete?.name}</strong>", currently used by {credentialForDelete?.companies.length} companies:
			<br />
			<strong>{credentialForDelete?.companies.map((_company) => _company.name).join(', ')}</strong>
			<br />
			This credential - including all recorded issue and expiration dates will no longer be visible on any laborer in these companies,
			and expiration reminders for it will stop.
		</>
	)
		, [credentialForDelete]);

	return (
		<>
			<Table
				buttons={buttons}
				columns={columns}
				fetch={fetchData}
				hasSearchInput={true}
				hasSubRows={false}
				offsetHeight={25}
				ref={tableRef}
				searchLabel="Credentials"
				tableName={TableNameEnum.ORGANIZATION_CREDENTIALS}
			/>
			<ConfirmationModal
				body={deleteDocumentModalTitle}
				closeModal={closeDeleteModal}
				confirmAction={confirmDeletion}
				confirmText={DELETE_DOCUMENT_TEXT}
				modalStyle="danger"
				showModal={!!credentialForDelete}
				title="Delete Item"
			/>
			<CredentialUpsertModal
				closeModal={closeUpsertCredentialModal}
				onConfirm={confirmCreationModal}
				row={credentialForUpdate}
				showModal={showCredentialModal}
			/>
		</>
	);
};

function mapDispatchToProps() {
	return {
		findOrganizationCredentialsTable: OrganizationCredentialActions.findOrganizationCredentialsTable,
		deleteOrganizationCredentials: OrganizationCredentialActions.deleteOrganizationCredentials,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<Props>>(
	React.memo,
	connector
);

export default enhance(OrganizationCredentials);
